export const DISPUTE_STATES = {
    DISPUTED: 'disputed',
    UNDER_REVIEW: 'under_review',
};

export const DISPUTE_RESOLUTION_TYPES = {
    UPHOLD: 'uphold',
    ADJUST: 'adjust',
    REFUND: 'refund',
};

const ACTIVE_DISPUTE_STATES = new Set(Object.values(DISPUTE_STATES));
const VALID_RESOLUTION_TYPES = new Set(Object.values(DISPUTE_RESOLUTION_TYPES));
const VALID_DISPUTE_ACTION_TYPES = new Set(['filed', 'under_review', 'upheld', 'adjusted', 'refunded']);

function nowIso() {
    return new Date().toISOString();
}

function requireTrimmed(value, label) {
    if (!value || !String(value).trim()) {
        throw new Error(`${label} is required`);
    }

    return String(value).trim();
}

function normalizeMoney(value, label) {
    if (value === '' || value === null || value === undefined) {
        return null;
    }

    const amount = Number(value);

    if (!Number.isFinite(amount) || amount <= 0) {
        throw new Error(`${label} must be a positive number`);
    }

    return Math.round(amount * 100) / 100;
}

export function hasActiveDispute(target) {
    return ACTIVE_DISPUTE_STATES.has(target?.dispute_state);
}

export function getDisputeReviewState(invoice) {
    if (hasActiveDispute(invoice)) {
        return invoice.dispute_state;
    }

    if (!Array.isArray(invoice?.line_items)) {
        return null;
    }

    if (invoice.line_items.some((lineItem) => lineItem?.dispute_state === DISPUTE_STATES.UNDER_REVIEW)) {
        return DISPUTE_STATES.UNDER_REVIEW;
    }

    if (invoice.line_items.some((lineItem) => lineItem?.dispute_state === DISPUTE_STATES.DISPUTED)) {
        return DISPUTE_STATES.DISPUTED;
    }

    return null;
}

export function fileDispute(target, { filedReason, filedAt = nowIso() }) {
    const reason = requireTrimmed(filedReason, 'filedReason');

    return {
        ...target,
        dispute_state: DISPUTE_STATES.DISPUTED,
        filed_reason: reason,
        filed_at: filedAt,
        resolved_by: null,
        resolved_at: null,
        resolution_type: null,
    };
}

export function startDisputeReview(target) {
    if (!hasActiveDispute(target)) {
        throw new Error('Cannot mark a non-disputed record under review');
    }

    return {
        ...target,
        dispute_state: DISPUTE_STATES.UNDER_REVIEW,
    };
}

export function resolveDispute(target, {
    resolutionType,
    resolvedBy,
    resolvedAt = nowIso(),
    resolutionAmount = null,
}) {
    if (!hasActiveDispute(target)) {
        throw new Error('Cannot resolve a record without an active dispute');
    }

    if (!VALID_RESOLUTION_TYPES.has(resolutionType)) {
        throw new Error(`Unknown resolutionType "${resolutionType}"`);
    }

    const actor = requireTrimmed(resolvedBy, 'resolvedBy');
    let amount = null;

    if (resolutionType !== DISPUTE_RESOLUTION_TYPES.UPHOLD) {
        if (resolutionAmount === '' || resolutionAmount === null || resolutionAmount === undefined) {
            throw new Error('resolutionAmount must be a positive number');
        }

        amount = normalizeMoney(resolutionAmount, 'resolutionAmount');
    }

    return {
        updatedTarget: {
            ...target,
            dispute_state: null,
            resolved_by: actor,
            resolved_at: resolvedAt,
            resolution_type: resolutionType,
        },
        resolutionAmount: amount,
    };
}

export function buildDisputeAuditEntry({
    orgId,
    invoiceId,
    actor,
    actionType,
    reason,
    lineItemId = null,
    fromState = null,
    toState = null,
    resolutionType = null,
    metadata = null,
}) {
    if (!VALID_DISPUTE_ACTION_TYPES.has(actionType)) {
        throw new Error(`Unknown actionType "${actionType}"`);
    }

    if (resolutionType !== null && !VALID_RESOLUTION_TYPES.has(resolutionType)) {
        throw new Error(`Unknown resolutionType "${resolutionType}"`);
    }

    return {
        org_id: requireTrimmed(orgId, 'orgId'),
        invoice_id: requireTrimmed(invoiceId, 'invoiceId'),
        line_item_id: lineItemId,
        action_type: actionType,
        actor: requireTrimmed(actor, 'actor'),
        reason: requireTrimmed(reason, 'reason'),
        from_state: fromState,
        to_state: toState,
        resolution_type: resolutionType,
        occurred_at: nowIso(),
        metadata,
    };
}

export function buildCreditNotePayload({
    orgId,
    invoice,
    createdBy,
    reason,
    refundAmount,
    lineItemId = null,
    creditNoteNumber = null,
}) {
    if (!invoice?.id) {
        throw new Error('invoice.id is required');
    }

    return {
        org_id: requireTrimmed(orgId, 'orgId'),
        source_invoice_id: invoice.id,
        source_line_item_id: lineItemId,
        credit_note_number: creditNoteNumber,
        refund_amount: normalizeMoney(refundAmount, 'refundAmount'),
        reason: requireTrimmed(reason, 'reason'),
        created_by: requireTrimmed(createdBy, 'createdBy'),
        created_at: nowIso(),
        source_invoice_number: invoice.invoice_number || null,
        source_confirmation_number: invoice.confirmation_number || null,
    };
}
